import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { User, Team, Activity } from '../types';
import { TeamSection } from './TeamSection';
import { CallsRow } from './CallsRow';

type ViewType = 'map' | 'status';

interface MapViewProps {
  users: User[];
  teams: Team[];
  workspaceTeamId?: string;
  activities?: Activity[];
  userEmail?: string;
}

const ADMIN_EMAIL = 'nick@chatbooks.com';

const API_BASE = process.env.NODE_ENV === 'production'
  ? ''
  : 'http://localhost:3001';

const MapContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  overflow: auto;
  position: relative;
`;

const MapContent = styled.div`
  min-height: 100vh;
  padding: 20px;
  position: relative;

  @media (min-width: 768px) {
    padding: 40px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Logo = styled.img`
  height: 40px;
  margin-bottom: 1rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));

  @media (min-width: 768px) {
    height: 55px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  background: rgba(0, 0, 0, 0.3);
  padding: 6px;
  border-radius: 25px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 8px 20px;
  border-radius: 20px;
  border: none;
  background: ${props => props.$active ? 'rgba(76, 175, 80, 0.9)' : 'transparent'};
  color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$active ? 'rgba(76, 175, 80, 1)' : 'rgba(255, 255, 255, 0.1)'};
    color: white;
  }

  @media (min-width: 768px) {
    padding: 10px 24px;
    font-size: 0.9rem;
  }
`;

const PauseButton = styled.button<{ $paused: boolean }>`
  padding: 5px 14px;
  border-radius: 12px;
  border: 1px solid ${props => props.$paused ? 'rgba(244, 67, 54, 0.5)' : 'rgba(255, 255, 255, 0.2)'};
  background: ${props => props.$paused ? 'rgba(244, 67, 54, 0.3)' : 'rgba(255, 255, 255, 0.08)'};
  color: ${props => props.$paused ? '#ff8a80' : 'rgba(255, 255, 255, 0.5)'};
  font-size: 0.7rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;

  &:hover {
    background: ${props => props.$paused ? 'rgba(244, 67, 54, 0.5)' : 'rgba(255, 255, 255, 0.15)'};
    color: ${props => props.$paused ? '#ff5252' : 'rgba(255, 255, 255, 0.8)'};
  }
`;

const FullWidthSection = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
`;

const TeamsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;

  @media (min-width: 600px) {
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 40px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 60px;
  }
`;

// Activity Feed styled components - matching the status card design
const ActivitySection = styled.div`
  background: rgba(76, 175, 80, 0.1);
  border-radius: 15px;
  padding: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(76, 175, 80, 0.3);
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.2);
  margin-bottom: 40px;

  @media (min-width: 768px) {
    border-radius: 20px;
    padding: 30px;
    box-shadow: 0 8px 32px rgba(76, 175, 80, 0.2);
  }
`;

const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const ActivityTitle = styled.h2`
  color: #ffffff;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const FilterPill = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? 'rgba(76, 175, 80, 0.8)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border: 1px solid ${props => props.$active ? 'rgba(76, 175, 80, 0.5)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 15px;
  padding: 6px 12px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$active ? 'rgba(76, 175, 80, 1)' : 'rgba(255, 255, 255, 0.2)'};
  }

  @media (min-width: 768px) {
    font-size: 0.8rem;
    padding: 6px 14px;
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  overflow: hidden;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  @media (min-width: 768px) {
    padding: 14px 20px;
    gap: 14px;
  }
`;

const ActivityIcon = styled.span`
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const ActivityInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  gap: 2px;
`;

const ActivityUserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActivityUser = styled.span`
  color: white;
  font-size: 0.9rem;
  font-weight: 600;

  @media (min-width: 768px) {
    font-size: 0.95rem;
  }
`;

const ActivityDetail = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ActivityTime = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
  flex-shrink: 0;
  margin-left: auto;

  @media (min-width: 768px) {
    font-size: 0.8rem;
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
`;

const SectionDivider = styled.div`
  margin: 40px 0;

  @media (min-width: 768px) {
    margin: 60px 0;
  }
`;

export const MapView: React.FC<MapViewProps> = ({ users, teams, workspaceTeamId, activities = [], userEmail }) => {
  const [activeView, setActiveView] = useState<ViewType>('map');
  const [activityFilter, setActivityFilter] = useState<'all' | 'status'>('all');
  const [isPaused, setIsPaused] = useState(false);

  const isAdmin = userEmail === ADMIN_EMAIL;

  useEffect(() => {
    if (!isAdmin) return;
    fetch(`${API_BASE}/api/admin/pause-status`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setIsPaused(data.paused); })
      .catch(() => {});
  }, [isAdmin]);

  const togglePause = useCallback(async () => {
    const endpoint = isPaused ? '/api/admin/resume' : '/api/admin/pause';
    try {
      const res = await fetch(`${API_BASE}${endpoint}`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setIsPaused(data.paused);
      }
    } catch {}
  }, [isPaused]);

  // Group users by teams
  const getUsersForTeam = (teamId: string) => {
    return users.filter(user => user.teams.includes(teamId));
  };

  // Separate General team from other teams
  const generalTeam = teams.find(team => team.id === 'default');
  const generalUsers = generalTeam ? getUsersForTeam(generalTeam.id) : [];

  // Separate status teams for full-span display
  const customStatusTeam = teams.find(team => team.id === 'custom-status');
  const customStatusUsers = customStatusTeam ? getUsersForTeam(customStatusTeam.id) : [];
  const outOfOfficeTeam = teams.find(team => team.id === 'out-of-office');
  const outOfOfficeUsers = outOfOfficeTeam ? getUsersForTeam(outOfOfficeTeam.id) : [];
  const meetingTeam = teams.find(team => team.id === 'meeting');
  const meetingUsers = meetingTeam ? getUsersForTeam(meetingTeam.id) : [];

  // Huddles and Zoom meetings render as a compact row above the team grid; Zoom first
  const isCall = (team: Team) => team.id.startsWith('huddle-') || team.id.startsWith('zoom-');
  const calls = teams
    .filter(team => isCall(team) && getUsersForTeam(team.id).length > 0)
    .sort((a, b) => {
      const zoomA = a.id.startsWith('zoom-');
      const zoomB = b.id.startsWith('zoom-');
      if (zoomA !== zoomB) return zoomA ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { numeric: true });
    });

  // Sort teams
  const regularTeams = teams
    .filter(team => {
      if (isCall(team)) return false;
      if (team.id === 'bookshelf') return false;
      if (team.id === 'custom-status') return false;
      if (team.id === 'out-of-office') return false;
      if (team.id === 'meeting') return false;
      return team.id !== 'default' && getUsersForTeam(team.id).length > 0;
    })
    .sort((a, b) => {
      const priorityOrder: { [key: string]: number } = {
        'bookshelf': 1,
        'custom-status': 2,
        'guests': 998,
        'out-of-office': 999,
        'meeting': 1000
      };

      const aPriority = priorityOrder[a.id] || 500;
      const bPriority = priorityOrder[b.id] || 500;

      if (aPriority !== 500 && bPriority !== 500) {
        return aPriority - bPriority;
      }

      if (aPriority !== 500) return aPriority < 500 ? -1 : 1;
      if (bPriority !== 500) return bPriority < 500 ? 1 : -1;

      return a.name.localeCompare(b.name);
    });

  // Activity helpers
  const filteredActivities = activityFilter === 'status'
    ? activities.filter(activity => activity.type === 'status_change')
    : activities;

  const getActivityIcon = (type: string, activity?: Activity) => {
    switch (type) {
      case 'status_change':
        if (activity) {
          const { oldStatusText, oldStatusEmoji, newStatusText, newStatusEmoji } = activity.data;
          const oldDisplay = `${oldStatusText || ''} ${oldStatusEmoji || ''}`.trim();
          const newDisplay = `${newStatusText || ''} ${newStatusEmoji || ''}`.trim();
          if (!oldDisplay && newDisplay) return '✨';
          if (oldDisplay && !newDisplay) return '🧹';
          return '💬';
        }
        return '💬';
      case 'presence_change':
        return activity?.data.isComingOnline ? '🟢' : '🌙';
      case 'huddle_join': return '🎧';
      case 'huddle_leave': return '👋';
      case 'zoom_join': return '🎥';
      case 'zoom_leave': return '📴';
      case 'team_change': return '🔄';
      default: return '📝';
    }
  };

  const getActivityDetail = (activity: Activity): string => {
    switch (activity.type) {
      case 'status_change':
        const { newStatusText, newStatusEmoji } = activity.data;
        const newDisplay = `${newStatusEmoji || ''} ${newStatusText || ''}`.trim();
        if (newDisplay) return newDisplay;
        return 'cleared status';

      case 'presence_change':
        return activity.data.isComingOnline ? 'came online' : 'went away';

      case 'huddle_join':
        const { huddleNumber } = activity.data;
        return huddleNumber ? `joined Huddle ${huddleNumber}` : 'joined a huddle';

      case 'huddle_leave':
        return 'left the huddle';

      case 'zoom_join':
        const { topic } = activity.data;
        return topic ? `joined ${topic}` : 'joined Zoom';

      case 'zoom_leave':
        return 'left Zoom';

      case 'team_change':
        const { addedTeams, removedTeams } = activity.data;
        if (addedTeams && addedTeams.length > 0) return `joined ${addedTeams[0]}`;
        if (removedTeams && removedTeams.length > 0) return `left ${removedTeams[0]}`;
        return 'changed teams';

      default:
        return 'updated profile';
    }
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - activityTime.getTime()) / 60000);

    if (diffInMinutes < 1) return 'now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return activityTime.toLocaleDateString();
  };

  const statusCount = customStatusUsers.length + outOfOfficeUsers.length + meetingUsers.length;

  return (
    <MapContainer>
      <MapContent>
        <Header>
          <Logo src="/chatbooks-logo.svg" alt="Chatbooks" />
          <TabContainer>
            <Tab $active={activeView === 'map'} onClick={() => setActiveView('map')}>
              Map
            </Tab>
            <Tab $active={activeView === 'status'} onClick={() => setActiveView('status')}>
              Status
            </Tab>
          </TabContainer>
          {isAdmin && (
            <PauseButton $paused={isPaused} onClick={togglePause}>
              {isPaused ? 'Resume Polling' : 'Pause Polling'}
            </PauseButton>
          )}
        </Header>

        {activeView === 'map' && (
          <>
            <CallsRow calls={calls} getUsersForTeam={getUsersForTeam} workspaceTeamId={workspaceTeamId} />

            <TeamsGrid>
              {regularTeams.map(team => (
                <TeamSection
                  key={team.id}
                  team={team}
                  users={getUsersForTeam(team.id)}
                  isFullWidth={false}
                  workspaceTeamId={workspaceTeamId}
                />
              ))}
            </TeamsGrid>

            {generalTeam && generalUsers.length > 0 && (
              <SectionDivider>
                <FullWidthSection>
                  <TeamSection
                    key="general"
                    team={generalTeam}
                    users={generalUsers}
                    isFullWidth={true}
                    workspaceTeamId={workspaceTeamId}
                  />
                </FullWidthSection>
              </SectionDivider>
            )}
          </>
        )}

        {activeView === 'status' && (
          <FullWidthSection>
            {/* Status Sections */}
            {statusCount > 0 ? (
              <TeamSection
                key="status-section"
                team={{ id: 'custom-status', name: 'Current Status', handle: 'status', description: '', users: [] }}
                users={customStatusUsers}
                secondaryUsers={outOfOfficeUsers}
                secondaryLabel="Out of Office"
                tertiaryUsers={meetingUsers}
                tertiaryLabel="In a Meeting"
                isFullWidth={true}
                workspaceTeamId={workspaceTeamId}
              />
            ) : (
              <EmptyState>No one has a status set right now</EmptyState>
            )}

            {/* Activity Feed Section */}
            <ActivitySection>
              <ActivityHeader>
                <ActivityTitle>Recent Activity</ActivityTitle>
                <FilterContainer>
                  <FilterPill
                    $active={activityFilter === 'all'}
                    onClick={() => setActivityFilter('all')}
                  >
                    All
                  </FilterPill>
                  <FilterPill
                    $active={activityFilter === 'status'}
                    onClick={() => setActivityFilter('status')}
                  >
                    Custom Statuses
                  </FilterPill>
                </FilterContainer>
              </ActivityHeader>

              {filteredActivities.length === 0 ? (
                <EmptyState>
                  {activityFilter === 'status' ? 'No custom status updates yet' : 'No recent activity'}
                </EmptyState>
              ) : (
                <ActivityList>
                  {filteredActivities.slice(0, 8).map((activity) => (
                    <ActivityItem key={activity.id}>
                      <ActivityIcon>{getActivityIcon(activity.type, activity)}</ActivityIcon>
                      <ActivityInfo>
                        <ActivityUserRow>
                          <ActivityUser>{activity.userName}</ActivityUser>
                          <ActivityTime>{getRelativeTime(activity.timestamp)}</ActivityTime>
                        </ActivityUserRow>
                        <ActivityDetail>{getActivityDetail(activity)}</ActivityDetail>
                      </ActivityInfo>
                    </ActivityItem>
                  ))}
                </ActivityList>
              )}
            </ActivitySection>
          </FullWidthSection>
        )}
      </MapContent>
    </MapContainer>
  );
};
